'use client';

import React, { useRef, useState } from 'react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  date: string;
  shootType: string;
  message: string;
}

interface FormErrors {
  email?: string;
  phone?: string;
  date?: string;
}

const SHOOT_TYPES = ['Portrait', 'Street', 'Event', 'Creative / Concept', 'Other'];

function validateEmail(value: string): string {
  if (!value) return 'Email is required.';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) return 'Please enter a valid email address.';
  return '';
}

function validatePhone(value: string): string {
  if (!value) return 'Phone number is required.';
  const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;
  if (!phoneRegex.test(value)) return 'Please enter a valid phone number.';
  return '';
}

function validateDate(value: string): string {
  if (!value) return 'Please select a preferred date.';
  const selected = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (selected < today) return 'Please choose a date in the future.';
  return '';
}

export default function BookingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    date: '',
    shootType: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Real-time validation for email, phone, date
    if (name === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) || undefined }));
    } else if (name === 'phone') {
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) || undefined }));
    } else if (name === 'date') {
      setErrors((prev) => ({ ...prev, date: validateDate(value) || undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) || undefined }));
    } else if (name === 'phone') {
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) || undefined }));
    } else if (name === 'date') {
      setErrors((prev) => ({ ...prev, date: validateDate(value) || undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submit
    const emailErr = validateEmail(form.email);
    const phoneErr = validatePhone(form.phone);
    const dateErr = validateDate(form.date);

    setErrors({ email: emailErr || undefined, phone: phoneErr || undefined, date: dateErr || undefined });
    setTouched({ email: true, phone: true, date: true });

    if (emailErr || phoneErr || dateErr) return;

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm({ name: '', email: '', phone: '', date: '', shootType: '', message: '' });
    setErrors({});
    setTouched({});
  };

  return (
    <section id="contact" ref={sectionRef} className="bg-ink py-24 px-6 md:px-10 lg:px-14">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <span className="section-label text-coral block mb-4">Book a Session</span>
          <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-24">
            <h2 className="font-mono font-bold text-concrete leading-[0.92]"
              style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
              LET&apos;S CREATE<br />
              SOMETHING<br />
              <span className="text-coral">REAL.</span>
            </h2>
            <p className="font-sans text-base text-concrete/55 leading-relaxed max-w-sm font-medium">
              Fill in the form and I&apos;ll get back to you as soon as possible. Every session starts with a conversation.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Form — 3 cols */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col gap-6 py-12">
                <div className="font-mono text-[3rem] text-coral font-bold">✓</div>
                <h3 className="font-mono font-bold text-concrete text-2xl">
                  Message received.
                </h3>
                <p className="font-sans text-concrete/55 leading-relaxed">
                  Thanks, {form.name}! I&apos;ll reach out to you soon to confirm your session details.
                </p>
                <button
                  onClick={resetForm}
                  className="btn-outline self-start"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-10" noValidate>
                {/* Name */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Maria Ionescu"
                    className="form-field"
                    autoComplete="name"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    placeholder="e.g. maria@example.com"
                    className={`form-field ${touched.email && errors.email ? 'border-red-500 focus:border-red-500' : touched.email && !errors.email ? 'border-green-500/60' : ''}`}
                    autoComplete="email"
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    aria-invalid={touched.email && !!errors.email}
                  />
                  {touched.email && errors.email && (
                    <span id="email-error" className="font-mono text-[0.6rem] text-red-400 tracking-wide" role="alert">
                      {errors.email}
                    </span>
                  )}
                  {touched.email && !errors.email && form.email && (
                    <span className="font-mono text-[0.6rem] text-green-400/70 tracking-wide">
                      ✓ Looks good
                    </span>
                  )}
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    placeholder="e.g. +40 753 427 542"
                    className={`form-field ${touched.phone && errors.phone ? 'border-red-500 focus:border-red-500' : touched.phone && !errors.phone ? 'border-green-500/60' : ''}`}
                    autoComplete="tel"
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                    aria-invalid={touched.phone && !!errors.phone}
                  />
                  {touched.phone && errors.phone && (
                    <span id="phone-error" className="font-mono text-[0.6rem] text-red-400 tracking-wide" role="alert">
                      {errors.phone}
                    </span>
                  )}
                  {touched.phone && !errors.phone && form.phone && (
                    <span className="font-mono text-[0.6rem] text-green-400/70 tracking-wide">
                      ✓ Looks good
                    </span>
                  )}
                </div>

                {/* Date */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`form-field ${touched.date && errors.date ? 'border-red-500 focus:border-red-500' : touched.date && !errors.date ? 'border-green-500/60' : ''}`}
                    style={{ colorScheme: 'dark' }}
                    aria-describedby={errors.date ? 'date-error' : undefined}
                    aria-invalid={touched.date && !!errors.date}
                  />
                  {touched.date && errors.date && (
                    <span id="date-error" className="font-mono text-[0.6rem] text-red-400 tracking-wide" role="alert">
                      {errors.date}
                    </span>
                  )}
                  {touched.date && !errors.date && form.date && (
                    <span className="font-mono text-[0.6rem] text-green-400/70 tracking-wide">
                      ✓ Date confirmed
                    </span>
                  )}
                </div>

                {/* Shoot type */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Type of Shoot
                  </label>
                  <select
                    name="shootType"
                    value={form.shootType}
                    onChange={handleChange}
                    required
                    className="form-field"
                  >
                    <option value="" disabled>Select a type…</option>
                    {SHOOT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/40">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Tell me about your vision, location, mood…"
                    className="form-field resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-coral self-start"
                  aria-label="Submit booking request"
                >
                  {loading ? 'Sending…' : 'Send Request →'}
                </button>
              </form>
            )}
          </div>

          {/* Contact info — 2 cols */}
          <div className="lg:col-span-2 flex flex-col gap-10 pt-2">
            <div className="flex flex-col gap-6">
              <span className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-concrete/30">
                Direct Contact
              </span>

              <div className="flex flex-col gap-4">
                <a
                  href="mailto:hvzphotosgl@gmail.com"
                  className="group flex flex-col gap-1"
                >
                  <span className="font-mono text-[0.55rem] tracking-[0.3em] uppercase text-coral/60">Email</span>
                  <span className="font-sans text-sm text-concrete/70 font-medium group-hover:text-concrete transition-colors break-all">
                    hvzphotosgl@gmail.com
                  </span>
                </a>

                <a
                  href="tel:+40753427542"
                  className="group flex flex-col gap-1"
                >
                  <span className="font-mono text-[0.55rem] tracking-[0.3em] uppercase text-coral/60">Phone</span>
                  <span className="font-sans text-sm text-concrete/70 font-medium group-hover:text-concrete transition-colors">
                    +40 753 427 542
                  </span>
                </a>

                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[0.55rem] tracking-[0.3em] uppercase text-coral/60">Location</span>
                  <span className="font-sans text-sm text-concrete/70 font-medium">
                    Galați, Romania
                  </span>
                </div>
              </div>
            </div>

            {/* Decorative quote */}
            <div className="border-l-2 border-coral/30 pl-5 mt-auto">
              <p className="font-mono text-sm font-bold text-concrete/40 leading-relaxed italic">
                &ldquo;Every frame is a feeling waiting to be found.&rdquo;
              </p>
              <span className="font-mono text-[0.55rem] tracking-[0.3em] uppercase text-concrete/25 block mt-2">
                — Seferis Toni
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
